import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { createRoot } from "react-dom/client";
import html2pdf from "html2pdf.js";
import { ArrowLeft, Download, FileText, FileSpreadsheet, FileJson, ChevronDown, Lock, CheckCircle, X } from "lucide-react";
import AuthModal from "@/components/AuthModal";

import { pages } from "@/Environment";
import { Button } from "@/components/ui/button";
import SummaryDisplay from "@/pages/view_grade/summaryDisplay";
import PDFTemplate from "@/components/pdf/PDFTemplate";
import { exportToCSV, exportToJSON } from "@/utils/exportFormats";
import { trackEvent } from "@/lib/analytics";
import "@/styles/pdf-export.css";

async function handleDownloadPDF(analysisData) {
  // Create a temporary container for PDF rendering
  const tempContainer = document.createElement("div");
  tempContainer.style.position = "fixed";
  tempContainer.style.left = "-9999px";
  tempContainer.style.top = "0";
  document.body.appendChild(tempContainer);

  // Render the PDF template (its own React root, so it needs its own motion setting:
  // no movement in the captured pages)
  const root = createRoot(tempContainer);
  const pdfElement = (
    <MotionConfig reducedMotion="always">
      <PDFTemplate analysisData={analysisData} />
    </MotionConfig>
  );

  return new Promise((resolve, reject) => {
    root.render(pdfElement);

    // Wait for rendering to complete (increased time for charts)
    setTimeout(async () => {
      try {
        // Headings use a web font: capture only once fonts have loaded (or failed)
        await document.fonts.ready;

        const element = tempContainer.firstChild;

        // Ensure the element has proper dimensions
        if (element) {
          element.style.width = '210mm';
          element.style.minHeight = '297mm';

          console.log('PDF content height:', element.scrollHeight);
          console.log('PDF sections:', element.querySelectorAll('section').length);
        }

        await html2pdf()
          .set({
            margin: 0,
            filename: `modern-slavery-analysis-${analysisData.company_info?.name || "report"}-${new Date().toISOString().split('T')[0]}.pdf`,
            image: { type: "jpeg", quality: 0.95 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              logging: false,
              letterRendering: true,
            },
            jsPDF: {
              unit: "mm",
              format: "a4",
              orientation: "portrait",
              compress: true,
            },
            pagebreak: { mode: ["css", "legacy"] },
          })
          .from(element)
          .save();

        resolve();
      } catch (error) {
        console.error('PDF generation error:', error);
        reject(error);
      } finally {
        // Cleanup
        root.unmount();
        document.body.removeChild(tempContainer);
      }
    }, 3000); // Increased timeout significantly for complex content and charts
  });
}

export default function ViewGrade({ analysisData, isGuestView = false, showWelcome = false }) {
  const pdfRef = useRef();
  const [downloadRequested, setDownloadRequested] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(showWelcome);

  // Auto-dismiss welcome banner after 5 seconds
  useEffect(() => {
    if (showWelcomeBanner) {
      const timer = setTimeout(() => {
        setShowWelcomeBanner(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showWelcomeBanner]);

  useEffect(() => {
    if (!downloadRequested) return;
    handleDownloadPDF(pdfRef);
    setDownloadRequested(false);
  }, [downloadRequested]);

  const handleExport = async (format) => {
    setShowExportMenu(false);
    setIsExporting(true);
    setExportError(null);

    try {
      if (format === 'pdf') {
        await handleDownloadPDF(analysisData);
      } else if (format === 'csv') {
        exportToCSV(analysisData);
      } else if (format === 'json') {
        exportToJSON(analysisData);
      }
      trackEvent('analysis_export', { format });
    } catch (error) {
      console.error('Export error:', error);
      setExportError('Failed to export. Please try again.');
      trackEvent('analysis_export_error', { format });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[var(--surface-ambient)] pb-24 pt-20">
      <div className="mx-auto flex max-w-6xl flex-col gap-10 px-6">
        <motion.header
          className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link to={pages.UPLOAD}>
            <Button variant="outline" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to upload
            </Button>
          </Link>

          <div className="relative">
            {isGuestView ? (
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={() => setShowAuthModal(true)}
              >
                <Lock className="h-4 w-4" aria-hidden="true" />
                Sign in to export
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={isExporting}
                  aria-label="Export menu"
                  aria-expanded={showExportMenu}
                  aria-haspopup="true"
                >
                  {isExporting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" aria-hidden="true" />
                      Export
                      <ChevronDown className="h-3 w-3" aria-hidden="true" />
                    </>
                  )}
                </Button>

                <AnimatePresence>
                  {showExportMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 rounded-2xl border border-[rgba(31,76,84,0.12)] bg-white p-2 shadow-[0_18px_36px_rgba(15,23,42,0.12)]"
                    >
                      <button
                        onClick={() => handleExport('pdf')}
                        className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm transition hover:bg-[var(--accent-light)]"
                      >
                        <FileText className="h-4 w-4 text-[var(--accent)]" aria-hidden="true" />
                        <div>
                          <p className="font-medium text-[var(--text-primary)]">PDF Report</p>
                          <p className="text-xs text-[var(--text-secondary)]">Executive briefing</p>
                        </div>
                      </button>

                      <button
                        onClick={() => handleExport('csv')}
                        className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm transition hover:bg-[var(--accent-light)]"
                      >
                        <FileSpreadsheet className="h-4 w-4 text-[var(--accent)]" aria-hidden="true" />
                        <div>
                          <p className="font-medium text-[var(--text-primary)]">CSV Export</p>
                          <p className="text-xs text-[var(--text-secondary)]">Scores and data</p>
                        </div>
                      </button>

                      <button
                        onClick={() => handleExport('json')}
                        className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm transition hover:bg-[var(--accent-light)]"
                      >
                        <FileJson className="h-4 w-4 text-[var(--accent)]" aria-hidden="true" />
                        <div>
                          <p className="font-medium text-[var(--text-primary)]">JSON Data</p>
                          <p className="text-xs text-[var(--text-secondary)]">Full analysis data</p>
                        </div>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
          </div>
        </motion.header>

        <AnimatePresence>
          {showWelcomeBanner && (
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-between rounded-2xl border border-[rgba(31,76,84,0.15)] bg-[var(--accent-light)] p-4"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--accent)]/10">
                  <CheckCircle className="h-5 w-5 text-[var(--accent)]" aria-hidden="true" />
                </div>
                <div>
                  <p className="font-medium text-[var(--text-primary)]">Welcome back</p>
                  <p className="text-sm text-[var(--text-secondary)]">Here's your full report with all benchmarks and insights</p>
                </div>
              </div>
              <button
                onClick={() => setShowWelcomeBanner(false)}
                className="rounded-lg p-1.5 text-[var(--text-secondary)] transition hover:bg-white/50 hover:text-[var(--text-primary)]"
                aria-label="Dismiss welcome message"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {exportError && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-[rgba(125,35,35,0.2)] bg-[rgba(125,35,35,0.05)] p-4"
          >
            <p className="text-sm text-danger-700">{exportError}</p>
          </motion.div>
        )}

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <SummaryDisplay
            ref={pdfRef}
            analysisData={analysisData}
            isGuestView={isGuestView}
            onUnlockRequest={() => setShowAuthModal(true)}
          />
        </motion.section>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </main>
  );
}
